import { IHttpService, IIntervalService, IPromise, IRootScopeService } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { EnvironmentService } from "@gtmhub/env";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IConfirmCloneOrMoveModal, IGoalCloneSelected, MethodType } from "@gtmhub/goals/controllers/clone-or-move-goal";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { localize } from "@gtmhub/localization";
import { CloneOkrsMode, IMetricCloneSelected } from "@gtmhub/okrs/components/clone-okrs/clone-okrs.component";
import {
  CloneRequest,
  CloneResourceRequest,
  ICloneJob,
  IMoveJob,
  JobId,
  JobStatus,
  MoveResourceRequest,
  NotificationInfo,
  ResourceType,
} from "@gtmhub/okrs/components/clone-okrs/clone-or-move.types";
import { OkrEvents } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { UiNotificationService } from "@webapp/ui/notification/services/notification.service";

export class CloneOrMoveResourceService {
  public static $inject = [
    "$http",
    "$rootScope",
    "$interval",
    "EnvironmentService",
    "UIErrorHandlingService",
    "MetricService",
    "GoalService",
    "UiNotificationService",
    "$uibModal",
  ];

  private okrEvents: OkrEvents;

  constructor(
    private $http: IHttpService,
    private $rootScope: IRootScopeService,
    private $interval: IIntervalService,
    private env: EnvironmentService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private metricService: MetricService,
    private goalService: GoalService,
    private uiNotificationService: UiNotificationService,
    private $uibModal: IModalService
  ) {
    this.okrEvents = new OkrEvents($rootScope);
  }

  public cloneSession(cloneRequest: CloneRequest): IPromise<void> {
    const payload: CloneResourceRequest = {
      type: ResourceType.Session,
      resourceId: cloneRequest.source,
      targetType: ResourceType.Session,
      targetResourceId: cloneRequest.target.sessionId,
      cloneChildren: !cloneRequest.target.cloneOnlyParentObjectives,
      cloneTasks: cloneRequest.target.cloneTasks,
      cloneTasksStatus: true,
      resetProgress: cloneRequest.target.resetProgress,
      // for session cloning the message is "Do not send notifications" when ticked
      sendNotifications: !cloneRequest.target.sendNotifications,
      keepCrossSessionRelationships: cloneRequest.target.shouldAlignSession,
    };
    return this.cloneInternal(payload, {
      targetSessionName: cloneRequest.target.title,
    });
  }

  public cloneGoal(source: IGoal, cloneOptions: IGoalCloneSelected): IPromise<void> {
    const targetType: ResourceType = cloneOptions.goal ? ResourceType.Objective : ResourceType.Session;
    const targetResourceId = cloneOptions.goal ? cloneOptions.goal.id : cloneOptions.session.id;

    const payload: CloneResourceRequest = {
      type: ResourceType.Objective,
      resourceId: source.id,
      targetType: targetType,
      targetResourceId: targetResourceId,
      cloneChildren: cloneOptions.cloneChildren,
      cloneTasks: cloneOptions.cloneTasks,
      cloneTasksStatus: true,
      resetProgress: !cloneOptions.progress,
      sendNotifications: cloneOptions.notifications,
      duplicateInsights: cloneOptions.duplicateInsight,
    };

    // you can select goal target and then select child session of the selected goal.
    // in that case we need to send additional parameter targetSessionId with the selection so you can keep the link
    if (cloneOptions.session && cloneOptions.goal) {
      payload.targetSessionId = cloneOptions.session.id;
    }

    return this.cloneInternal(payload, { targetSessionName: cloneOptions.session.title });
  }

  public cloneMetric(source: Metric, cloneOptions: IMetricCloneSelected): IPromise<void> {
    const payload: CloneResourceRequest = {
      type: ResourceType.KeyResult,
      resourceId: source.id,
      targetType: ResourceType.Objective,
      targetResourceId: cloneOptions.goal.id,
      resetProgress: !cloneOptions.progress,
      sendNotifications: cloneOptions.notifications,
      keepUpdatesInSync: cloneOptions.sync,
      cloneTasks: true,
      cloneTasksStatus: true,
      duplicateInsights: cloneOptions.duplicateInsight,
    };

    return this.cloneInternal(payload, { targetGoalName: cloneOptions.goal.name });
  }

  public moveGoal(source: IGoal, selectedContext: IGoalCloneSelected): IPromise<void> {
    const selectedGoal = selectedContext.goal;
    const selectedSession = selectedContext.session;

    const resourceTargetType = selectedGoal ? ResourceType.Objective : ResourceType.Session;
    const targetId = selectedGoal ? selectedGoal.id : selectedSession.id;

    const moveRequest: MoveResourceRequest = {
      type: ResourceType.Objective,
      resourceId: source.id,
      targetType: resourceTargetType,
      targetResourceId: targetId,
      targetSessionId: selectedSession.id,
      moveChildren: selectedContext.moveChildren,
    };

    return this.moveInternal(moveRequest, selectedContext);
  }

  public confirmAction = (methodType: MethodType, cfCounter: number, mode?: CloneOkrsMode): IPromise<void> => {
    const invokerType = mode ? mode : "OKR";
    return this.$uibModal.open({
      template: require("./confirm-clone-or-move.html"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IConfirmCloneOrMoveModal) {
          $scope.method = methodType;
          $scope.cfCounter = cfCounter;
          $scope.invokerType = invokerType;
        },
      ],
    }).result;
  };

  public moveInternal(request: MoveResourceRequest, selectedContext: IGoalCloneSelected): IPromise<void> {
    // create job item
    const inProgressNotification = this.uiNotificationService.info(localize("moving_in_progress"), localize("moving_in_progress"), {
      notificationDuration: 1000 * 60 * 5, // 5 min as this should be removed at some point
      notificationPlacement: "bottomRight",
    });

    return this.scheduleMoveJob(request)
      .then((jobId: JobId) => {
        /** Ping the move api every 3 seconds until the status is done or reverted. */
        const retryJobStatusInterval = this.$interval(() => {
          this.fetchMoveJobById(jobId)
            .then((job) => {
              const done = JobStatus.Done === job.status;
              const reverted = JobStatus.Reverted == job.status || JobStatus.Reverting == job.status || JobStatus.ToBeReverted == job.status;
              if (done) {
                // remove in progress notification
                this.uiNotificationService.remove(inProgressNotification.messageId);
                // stop the pinging
                this.$interval.cancel(retryJobStatusInterval);
                this.goalService
                  .getGoal(job.rootMovedId)
                  .then((newGoal) => {
                    const content = `"${newGoal.name}" ${localize("has_been_successfully")} ${localize("moved")} ${localize("in_preposition")} "${
                      selectedContext.session.title
                    }" `;
                    this.$rootScope.$broadcast("goalsChanged");
                    this.okrEvents.broadcastGoalsChanged("goalChanged");
                    this.okrEvents.broadcastGoalsChanged("goalMoved", { goal: newGoal });
                    this.showSuccessNotification(localize("done"), content);
                  })
                  .catch((error) => {
                    this.handleError(error);
                  });
              } else if (reverted) {
                this.uiNotificationService.remove(inProgressNotification.messageId);
                this.$interval.cancel(retryJobStatusInterval);
                this.showErrorNotification(localize("move_job_reverted_title"), localize("move_job_reverted_message"));
              }
            })
            .catch((error) => {
              this.$interval.cancel(retryJobStatusInterval);
              this.handleError(error);
            });
        }, 3000);
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  public cloneInternal(payload: CloneResourceRequest, notificationInfo?: NotificationInfo): IPromise<void> {
    const inProgressNotification = this.uiNotificationService.info(localize("copying_in_progress"), localize("copying_in_progress"), {
      notificationDuration: 1000 * 60 * 5, // 5 min
      notificationPlacement: "bottomRight",
    });

    // create job item
    return this.scheduleCloneJob(payload)
      .then((cloneJobId: JobId) => {
        /** Ping the clone api every 3 seconds until the status is done or reverted. */
        const retryJobStatusInterval = this.$interval(() => {
          this.fetchCloneJobById(cloneJobId)
            .then((cloneJob) => {
              const done = JobStatus.Done === cloneJob.status;
              const reverted = JobStatus.Reverted == cloneJob.status || JobStatus.Reverting == cloneJob.status || JobStatus.ToBeReverted == cloneJob.status;
              if (done) {
                this.uiNotificationService.remove(inProgressNotification.messageId);
                this.$interval.cancel(retryJobStatusInterval);
                this.handleCloneSuccess(payload.type, cloneJob, notificationInfo);
              } else if (reverted) {
                this.uiNotificationService.remove(inProgressNotification.messageId);
                this.$interval.cancel(retryJobStatusInterval);
                this.showErrorNotification(localize("clone_job_reverted_title"), localize("clone_job_reverted_message"));
              }
            })
            .catch((error) => {
              this.$interval.cancel(retryJobStatusInterval);
              this.handleError(error);
            });
        }, 3000);
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  private scheduleCloneJob(cloneRequest: CloneResourceRequest): IPromise<JobId> {
    const url = this.env.getApiEndpoint("/clone-jobs");
    return this.$http.post<JobId>(url, cloneRequest).then((response) => {
      return response.data;
    });
  }

  private scheduleMoveJob(moveResourceRequest: MoveResourceRequest): IPromise<JobId> {
    const url = this.env.getApiEndpoint("/move-jobs");
    return this.$http.post<JobId>(url, moveResourceRequest).then((response) => {
      return response.data;
    });
  }

  private fetchCloneJobById(target: JobId): IPromise<ICloneJob> {
    const url = this.env.getApiEndpoint("/clone-jobs/" + target.id);
    return this.$http.get<ICloneJob>(url).then((response) => {
      return response.data;
    });
  }

  private fetchMoveJobById(target: JobId): IPromise<IMoveJob> {
    const url = this.env.getApiEndpoint("/move-jobs/" + target.id);
    return this.$http.get<IMoveJob>(url).then((response) => {
      return response.data;
    });
  }

  private handleCloneSuccess(type: ResourceType, cloneJob: ICloneJob, notificationInfo?: NotificationInfo): void {
    let content = "";
    let clonedItemsCount = 0;
    const clonedGoalsIds: string[] = [];

    switch (type) {
      case ResourceType.Session:
        clonedItemsCount = Object.values(cloneJob.clonedIds).filter((item) => ["okr", "kr", "task"].includes(item)).length;
        content = `${clonedItemsCount} ${clonedItemsCount == 1 ? localize("item") : localize("items")}
          ${clonedItemsCount == 1 ? localize("has_been_successfully") : localize("have_been_successfully")} ${localize("copied")} ${localize("to")} "${
          notificationInfo.targetSessionName
        }"`;
        this.showSuccessNotification(localize("done"), content);
        break;
      case ResourceType.Objective:
        for (const goalId in cloneJob.clonedIds) {
          if (Object.prototype.hasOwnProperty.call(cloneJob.clonedIds, goalId) && cloneJob.clonedIds[goalId] === ResourceType.Objective) {
            clonedGoalsIds.push(goalId);
          }
        }

        this.goalService
          .getGoalsV2({
            filter: {
              $and: [{ _id: { $in: clonedGoalsIds } }],
            },
          })
          .then(({ items }) => {
            const rootClonedGoal = items.find((goal) => goal.id === cloneJob.rootClonedId);

            if (items.length > 1) {
              this.okrEvents.broadcastGoalsChanged("goalsCloned", { goals: items });
            } else {
              this.okrEvents.broadcastGoalsChanged("goalCloned", { goal: items[0] });
            }

            content = `"${rootClonedGoal.name}" ${localize("has_been_successfully")} ${localize("copied")} ${localize("in_preposition")} "${
              notificationInfo.targetSessionName
            }" `;
            this.$rootScope.$broadcast("goalsChanged");
            this.showSuccessNotification(localize("done"), content);
          })
          .catch((error) => {
            this.handleError(error);
          });
        break;
      case ResourceType.KeyResult:
        this.metricService
          .getMetric(cloneJob.rootClonedId)
          .then((newMetric) => {
            this.okrEvents.broadcastGoalsChanged("goalChanged");
            this.$rootScope.$broadcast("goalsChanged");
            this.okrEvents.broadcastGoalsChanged("metricCreated", { goalId: newMetric.goalId, metric: newMetric });
            content = `"${newMetric.name}" ${localize("has_been_successfully")} ${localize("copied")} ${localize("to")} "${notificationInfo.targetGoalName}"`;
            this.showSuccessNotification(localize("done"), content);
          })
          .catch((error) => {
            this.handleError(error);
          });
        break;
    }
  }

  private handleError(err: IUIError): void {
    this.uiNotificationService.remove();
    this.uiErrorHandlingService.handleModal(err);
  }

  private showSuccessNotification(title: string, content: string) {
    this.uiNotificationService.success(title, content, {
      notificationDuration: 6000,
      notificationPlacement: "bottomRight",
    });
  }

  private showErrorNotification(title: string, content: string) {
    this.uiNotificationService.error(title, content, {
      notificationDuration: 6000,
      notificationPlacement: "bottomRight",
    });
  }
}
